// Today's shifts: who is on now, and who has finished.
//
// From attendance (Tanda, mirrored into hr.attendance), not pod timers. Timers
// said who touched a pod and read badly as a shift: a trolley run logged a
// hundred-odd, someone between pods showed as gone, and a night shift looked
// like it started at midnight. A shift has a clock-in and a clock-out, and the
// hours between them are what people actually ask this sheet for.
//
// An open shift's hours are counted here, against the server's clock, and tick
// by the minute. A finished shift shows Odoo's own worked_hours rather than
// out-minus-in, so it agrees with what payroll will see — Odoo takes the unpaid
// break out and this sheet should not argue with it.

import type { ReactNode } from 'react';
import { employeeAvatarUrl } from '@/api/client';
import type { CrewShiftVM } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { AvatarTile, EmptyState, Skeleton } from '@/components/ui/misc';
import { useNow } from '@/hooks/useElapsed';
import { useT } from '@/i18n/useT';
import { fmtLocalTime } from '@/lib/pace';
import { useCrewToday } from '@/queries';
import { useStore } from '@/store';

/** Hours of an open shift, from its naive-UTC check-in to the server's now. */
export function openShiftHours(checkIn: string, serverNow: number): number {
  const at = Date.parse(checkIn.replace(' ', 'T') + 'Z');
  if (Number.isNaN(at)) return 0;
  return Math.max(0, (serverNow - at) / 3600_000);
}

/** 7.5 → "7h 30m", 0.2 → "12m". */
export function fmtShiftHours(hours: number): string {
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

export function CrewSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const crew = useCrewToday(open);
  const skew = useStore((s) => s.serverSkewMs);
  const now = useNow(30_000, open) - skew;
  const shifts = crew.data ?? [];
  const onNow = shifts.filter((s) => s.open);
  // Most recent finish first: the question is usually "who just went home".
  const finished = shifts.filter((s) => !s.open).sort((a, b) => (b.checkOut ?? '').localeCompare(a.checkOut ?? ''));

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.shiftsTodayT} hideTitle>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', font: "600 12px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>{t.fromAttendanceKickerT}</span>
          <h2 style={{ font: "700 19px 'Space Grotesk',sans-serif", margin: '2px 0 0' }}>
            {onNow.length} {t.onShiftNowT}
          </h2>
        </span>
        <span
          style={{
            borderRadius: 7,
            padding: '3px 9px',
            font: '700 10.5px Manrope,sans-serif',
            background: 'var(--surface2)',
            color: 'var(--sub)',
            flex: 'none',
            marginTop: 4,
          }}
        >
          {finished.length} {t.finishedCountT}
        </span>
      </div>

      {crew.isPending ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
      ) : shifts.length === 0 ? (
        <EmptyState>{t.noShiftsTodayT}</EmptyState>
      ) : (
        <>
          {onNow.length > 0 && (
            <ShiftSection label={t.onShiftNowHeadT}>
              {onNow.map((s) => (
                <ShiftRow key={s.attendanceId} shift={s} hours={openShiftHours(s.checkIn, now)} />
              ))}
            </ShiftSection>
          )}
          {finished.length > 0 && (
            <ShiftSection label={t.shiftFinishedTodayT}>
              {finished.map((s) => (
                <ShiftRow key={s.attendanceId} shift={s} hours={s.workedHours} />
              ))}
            </ShiftSection>
          )}
        </>
      )}

      <div style={{ marginTop: 16 }}>
        <Button variant="surface" size="md" onClick={onClose}>
          {t.dismiss}
        </Button>
      </div>
    </Sheet>
  );
}

function ShiftSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section aria-label={label} style={{ marginBottom: 14 }}>
      <div className="sectionLabel">{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
    </section>
  );
}

function ShiftRow({ shift: s, hours }: { shift: CrewShiftVM; hours: number | null }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, borderTop: '1px solid var(--surface2)', padding: '6px 0' }}>
      <span
        aria-hidden="true"
        style={{ width: 10, height: 10, borderRadius: '50%', background: s.open ? 'var(--pos)' : 'var(--line)', flex: 'none' }}
      />
      <AvatarTile name={s.name} size={30} photoUrl={employeeAvatarUrl(s.employeeId)} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: '600 12.5px Manrope,sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {s.name}
        </span>
        <span className="numeric" style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)' }}>
          {fmtLocalTime(s.checkIn)} – {s.open ? t.nowT : fmtLocalTime(s.checkOut)}
          {s.department ? ` · ${s.department}` : ''}
        </span>
      </span>
      <span style={{ textAlign: 'right', flex: 'none' }}>
        <span className="numeric" style={{ display: 'block', font: '700 12.5px Manrope,sans-serif', color: s.open ? 'var(--pos)' : undefined }}>
          {hours == null ? '—' : fmtShiftHours(hours)}
        </span>
        {s.open && <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)' }}>{t.soFarT}</span>}
      </span>
    </div>
  );
}
